import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import {
  FileText, Send, Lightbulb, Plus, Sparkles, Edit, Trash2, CheckCircle,
  Clock, Eye, Calendar, Hash, Target, MessageSquare, PenTool, X, 
  Image, Palette, Copy, Download, LayoutGrid, ArrowRight, Lock, Play, Rocket, Wand2
} from 'lucide-react';
import DesignEditor from '../../components/marketing/DesignEditor';
import { PermissionButton, PermissionGate, usePermissions, FEATURE_PERMISSIONS } from '../../components/common/PermissionButton';
import { ApprovalPanel, ApprovalStatusBadge } from '../../components/marketing/ApprovalPanel';
import DryRunPublish from '../../components/marketing/DryRunPublish';
import LivePublish from '../../components/marketing/LivePublish';
import AIGeneratePanel from '../../components/marketing/AIGeneratePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  new: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
  idea: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  outline: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  draft: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  review: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  approved: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  scheduled: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
  published: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400',
  pending_approval: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
};

const platformIcons = {
  facebook: '📘',
  instagram: '📷',
  linkedin: '💼',
  twitter: '🐦',
  google_business: '📍',
  tiktok: '🎵'
};

// Ideas Tab Component
const IdeasTab = ({ ideas, onCreateIdea, onDeleteIdea }) => {
  const [newIdea, setNewIdea] = useState('');
  const [priority, setPriority] = useState('medium');

  const handleCreate = () => {
    if (!newIdea.trim()) return;
    onCreateIdea({ prompt: newIdea, priority });
    setNewIdea('');
  };

  return (
    <div className="space-y-4">
      <Card className="dark:bg-slate-800/60">
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Capture a content idea..."
                value={newIdea}
                onChange={(e) => setNewIdea(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                className="dark:bg-slate-700"
              />
            </div>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3">
        {ideas.map(idea => (
          <Card key={idea.id} className="dark:bg-slate-800/60">
            <CardContent className="p-4 flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-yellow-500 mt-0.5" />
                <div>
                  <p className="dark:text-white">{idea.prompt}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge className={statusColors[idea.status]}>{idea.status}</Badge>
                    <Badge variant="outline">{idea.priority}</Badge>
                    {idea.target_channel && <Badge variant="secondary">{idea.target_channel}</Badge>}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onDeleteIdea(idea.id)}>
                <Trash2 className="h-4 w-4 text-rose-500" />
              </Button>
            </CardContent>
          </Card>
        ))}
        {ideas.length === 0 && (
          <div className="text-center py-12 text-muted-foreground dark:text-slate-400">
            <Lightbulb className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No ideas yet. Start capturing your content ideas!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Blog Posts Tab Component
const BlogPostsTab = ({ posts, onCreatePost, onDeletePost, onEdit }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', slug: '', category: '', status: 'idea' });
  const [generating, setGenerating] = useState(false);

  const handleCreate = () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    onCreatePost(formData);
    setFormData({ title: '', slug: '', category: '', status: 'idea' });
    setDialogOpen(false);
  };

  const generateOutline = async () => {
    if (!formData.title) {
      toast.error('Enter a title first');
      return;
    }
    setGenerating(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await axios.post(
        `${API}/marketing/ai/generate-blog-outline?topic=${encodeURIComponent(formData.title)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.outline) {
        setFormData(prev => ({ ...prev, outline: res.data.outline }));
        toast.success('Outline generated!');
      }
    } catch (error) {
      toast.error('Failed to generate outline');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="h-4 w-4 mr-2" /> New Blog Post
        </Button>
      </div>

      <div className="grid gap-4">
        {posts.map(post => (
          <Card key={post.id} className="dark:bg-slate-800/60">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium dark:text-white flex items-center gap-2">
                    <FileText className="h-4 w-4 text-purple-500" />
                    {post.title}
                  </h3>
                  {post.slug && <p className="text-sm text-muted-foreground dark:text-slate-400 mt-1">/{post.slug}</p>}
                  <div className="flex gap-2 mt-2">
                    <Badge className={statusColors[post.status]}>{post.status}</Badge>
                    {post.category && <Badge variant="outline">{post.category}</Badge>}
                    {post.approval_status === 'pending' && (
                      <Badge className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700">Needs Approval</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm"><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onDeletePost(post.id)}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {posts.length === 0 && (
          <div className="text-center py-12 text-muted-foreground dark:text-slate-400">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No blog posts yet. Create your first article!</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white">
              <FileText className="h-5 w-5 text-purple-500" />
              New Blog Post
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter blog post title"
                className="dark:bg-slate-700"
              />
            </div>
            <div>
              <Label className="dark:text-slate-200">URL Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="url-friendly-slug"
                className="dark:bg-slate-700"
              />
            </div>
            <div>
              <Label className="dark:text-slate-200">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger className="dark:bg-slate-700">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tips">Tips & Guides</SelectItem>
                  <SelectItem value="projects">Project Showcases</SelectItem>
                  <SelectItem value="news">Company News</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={generateOutline} disabled={generating} className="w-full">
              <Sparkles className="h-4 w-4 mr-2" />
              {generating ? 'Generating...' : 'Generate AI Outline'}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-purple-600 hover:bg-purple-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Social Posts Tab Component
const SocialPostsTab = ({ posts, onCreatePost, onDeletePost }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ platform: 'facebook', post_copy: '', hashtags: [] });
  const [hashtagInput, setHashtagInput] = useState('');

  const handleCreate = () => {
    if (!formData.post_copy) {
      toast.error('Post content is required');
      return;
    }
    onCreatePost(formData);
    setFormData({ platform: 'facebook', post_copy: '', hashtags: [] });
    setDialogOpen(false);
  };

  const addHashtag = () => {
    if (hashtagInput.trim()) {
      const tag = hashtagInput.startsWith('#') ? hashtagInput : `#${hashtagInput}`;
      if (!formData.hashtags.includes(tag)) {
        setFormData(prev => ({ ...prev, hashtags: [...prev.hashtags, tag] }));
      }
      setHashtagInput('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> New Social Post
        </Button>
      </div>

      <div className="grid gap-4">
        {posts.map(post => (
          <Card key={post.id} className="dark:bg-slate-800/60">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{platformIcons[post.platform] || '📱'}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium dark:text-white capitalize">{post.platform}</span>
                    <Badge className={statusColors[post.status]}>{post.status}</Badge>
                    {post.scheduled_at && (
                      <span className="text-xs text-muted-foreground dark:text-slate-400 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(post.scheduled_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-sm dark:text-slate-300 mt-2 line-clamp-3">{post.post_copy}</p>
                  {post.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {post.hashtags.map((tag, idx) => (
                        <span key={idx} className="text-xs text-blue-500">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm"><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onDeletePost(post.id)}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {posts.length === 0 && (
          <div className="text-center py-12 text-muted-foreground dark:text-slate-400">
            <Send className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No social posts yet. Create engaging content for your channels!</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white">
              <Send className="h-5 w-5 text-emerald-500" />
              New Social Post
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Platform</Label>
              <Select
                value={formData.platform}
                onValueChange={(v) => setFormData({ ...formData, platform: v })}
              >
                <SelectTrigger className="dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="facebook">📘 Facebook</SelectItem>
                  <SelectItem value="instagram">📷 Instagram</SelectItem>
                  <SelectItem value="linkedin">💼 LinkedIn</SelectItem>
                  <SelectItem value="twitter">🐦 Twitter/X</SelectItem>
                  <SelectItem value="google_business">📍 Google Business</SelectItem>
                  <SelectItem value="tiktok">🎵 TikTok</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="dark:text-slate-200">Post Content</Label>
              <Textarea
                value={formData.post_copy}
                onChange={(e) => setFormData({ ...formData, post_copy: e.target.value })}
                placeholder="Write your post content..."
                rows={4}
                className="dark:bg-slate-700"
              />
            </div>
            <div>
              <Label className="dark:text-slate-200">Hashtags</Label>
              <div className="flex gap-2">
                <Input
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                  placeholder="Add hashtag"
                  className="dark:bg-slate-700"
                />
                <Button type="button" variant="outline" onClick={addHashtag}>
                  <Hash className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.hashtags.map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1">
                    {tag}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setFormData(prev => ({
                      ...prev,
                      hashtags: prev.hashtags.filter((_, i) => i !== idx)
                    }))} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ContentStudio = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'designs');
  const [ideas, setIdeas] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Design editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [currentDesign, setCurrentDesign] = useState(null);
  const [designPresets, setDesignPresets] = useState({});
  const [newDesignDialog, setNewDesignDialog] = useState(false);
  const [newDesignForm, setNewDesignForm] = useState({ name: '', design_type: 'facebook_post' });
  const [showDryRunDialog, setShowDryRunDialog] = useState(false);  // Phase 8A
  const [showLivePublishDialog, setShowLivePublishDialog] = useState(false);  // Phase 8B
  const [showAIPanel, setShowAIPanel] = useState(false);  // Phase 9: AI Marketing Intelligence
  const [currentCaption, setCurrentCaption] = useState('');  // Phase 9: For AI improvement

  // Phase 3.5: Permission checks
  const permissions = usePermissions([
    FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE,
    FEATURE_PERMISSIONS.CONTENT_STUDIO_EDIT,
    FEATURE_PERMISSIONS.CONTENT_STUDIO_DELETE,
    FEATURE_PERMISSIONS.CONTENT_STUDIO_TEMPLATES
  ]);

  const canCreate = permissions[FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE];
  const canEdit = permissions[FEATURE_PERMISSIONS.CONTENT_STUDIO_EDIT];
  const canDelete = permissions[FEATURE_PERMISSIONS.CONTENT_STUDIO_DELETE];
  const canEditTemplates = permissions[FEATURE_PERMISSIONS.CONTENT_STUDIO_TEMPLATES];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [ideasRes, blogRes, socialRes, designsRes, templatesRes, presetsRes] = await Promise.all([
        axios.get(`${API}/marketing/ideas`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/blog-posts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/social-posts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/designs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/designs/templates`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/designs/presets`, { headers }).catch(() => ({ data: {} }))
      ]);

      setIdeas(ideasRes.data || []);
      setBlogPosts(blogRes.data || []);
      setSocialPosts(socialRes.data || []);
      setDesigns(designsRes.data || []);
      setTemplates(templatesRes.data || []);
      setDesignPresets(presetsRes.data || {});
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createIdea = async (data) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/ideas`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Idea captured!');
      fetchData();
    } catch (error) {
      toast.error('Failed to create idea');
    }
  };

  const deleteIdea = async (id) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.delete(`${API}/marketing/ideas/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Idea deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete idea');
    }
  };

  const createBlogPost = async (data) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/blog-posts`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Blog post created!');
      fetchData();
    } catch (error) {
      toast.error('Failed to create blog post');
    }
  };

  const deleteBlogPost = async (id) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.delete(`${API}/marketing/blog-posts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Blog post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const createSocialPost = async (data) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/social-posts`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Social post created!');
      fetchData();
    } catch (error) {
      toast.error('Failed to create social post');
    }
  };

  const deleteSocialPost = async (id) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.delete(`${API}/marketing/social-posts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Social post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete social post');
    }
  };

  // ========== DESIGN FUNCTIONS ==========
  
  const createDesign = async (designData) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const preset = designPresets[designData.design_type] || { width: 1200, height: 628 };
      
      const newDesign = {
        name: designData.name || 'Untitled Design',
        design_type: designData.design_type,
        canvas_width: preset.width,
        canvas_height: preset.height,
        canvas_background: '#FFFFFF',
        elements: []
      };
      
      const response = await axios.post(`${API}/marketing/designs`, newDesign, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Design created!');
      setCurrentDesign(response.data);
      setIsEditorOpen(true);
      setNewDesignDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create design');
      console.error(error);
    }
  };

  const openDesign = async (design) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.get(`${API}/marketing/designs/${design.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentDesign(response.data);
      setIsEditorOpen(true);
    } catch (error) {
      toast.error('Failed to open design');
    }
  };

  const saveDesign = async (designData) => {
    if (!currentDesign?.id) return;
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.patch(`${API}/marketing/designs/${currentDesign.id}`, {
        elements: designData.elements,
        canvas_width: designData.canvas_width,
        canvas_height: designData.canvas_height,
        canvas_background: designData.canvas_background
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Design saved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to save design');
    }
  };

  const deleteDesign = async (id) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.delete(`${API}/marketing/designs/${id}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Design deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete design');
    }
  };

  const duplicateDesign = async (id) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.post(`${API}/marketing/designs/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Design duplicated!');
      fetchData();
      // Open the duplicated design
      setCurrentDesign(response.data);
      setIsEditorOpen(true);
    } catch (error) {
      toast.error('Failed to duplicate design');
    }
  };

  const createTemplateFromDesign = async (designId, templateName) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/designs/${designId}/create-template?template_name=${encodeURIComponent(templateName)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template created!');
      fetchData();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleScheduleDesign = (designData, previewImage) => {
    // Navigate to scheduler with design data
    const scheduleData = {
      content_type: 'design',
      design_id: currentDesign?.id,
      name: currentDesign?.name,
      preview_image: previewImage
    };
    
    // Store in sessionStorage for scheduler to pick up
    sessionStorage.setItem('scheduled_content', JSON.stringify(scheduleData));
    toast.success('Redirecting to scheduler...');
    navigate('/marketing-hub/scheduler?action=schedule');
  };

  const handleExportDesign = (exportUri) => {
    // Could save the export URI to the design record
    console.log('Design exported:', exportUri);
  };

  // Create design from template
  const createFromTemplate = async (template) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.post(`${API}/marketing/designs/${template.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Design created from template!');
      fetchData();
      setCurrentDesign(response.data);
      setIsEditorOpen(true);
    } catch (error) {
      toast.error('Failed to create from template');
    }
  };

  return (
    <Layout>
      {/* Design Editor Full Screen Dialog */}
      {isEditorOpen && (
        <div className="fixed inset-0 z-50 bg-slate-100 dark:bg-slate-900">
          <div className="flex flex-col h-full">
            <div className="h-12 bg-white dark:bg-slate-800 border-b dark:border-slate-700 flex items-center px-4 gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setIsEditorOpen(false);
                  setCurrentDesign(null);
                  setShowAIPanel(false);
                }}
              >
                <X className="h-4 w-4 mr-2" /> Close
              </Button>
              <div className="flex-1">
                <h2 className="font-semibold dark:text-white">{currentDesign?.name || 'New Design'}</h2>
              </div>
              {/* Phase 9: AI Assistant Button */}
              <Button
                variant={showAIPanel ? "secondary" : "outline"}
                size="sm"
                onClick={() => setShowAIPanel(!showAIPanel)}
                className="ml-2"
                data-testid="ai-assistant-btn"
              >
                <Wand2 className="h-4 w-4 mr-1" />
                AI Assistant
              </Button>
              {/* Phase 7: Show approval status in editor header */}
              {currentDesign?.approval_status && currentDesign.approval_status !== 'not_submitted' && (
                <ApprovalStatusBadge status={currentDesign.approval_status} />
              )}
              {/* Phase 8A: Dry Run Publish Button */}
              {currentDesign?.id && currentDesign?.approval_status === 'approved' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDryRunDialog(true)}
                  className="ml-2"
                  data-testid="dry-run-btn"
                >
                  <Play className="h-4 w-4 mr-1" />
                  Dry Run
                </Button>
              )}
              {/* Phase 8B: Live Publish Button */}
              {currentDesign?.id && currentDesign?.approval_status === 'approved' && (
                <Button
                  size="sm"
                  onClick={() => setShowLivePublishDialog(true)}
                  className="ml-2 bg-green-600 hover:bg-green-700"
                  data-testid="live-publish-btn"
                >
                  <Rocket className="h-4 w-4 mr-1" />
                  Publish
                </Button>
              )}
            </div>
            <div className="flex-1 flex gap-4">
              <div className="flex-1">
                <DesignEditor
                  design={currentDesign}
                  onSave={saveDesign}
                  onExport={handleExportDesign}
                  onSchedule={handleScheduleDesign}
                />
              </div>
              {/* Right Side Panels */}
              {currentDesign?.id && (
                <div className="w-80 flex-shrink-0 overflow-y-auto bg-white dark:bg-slate-800 border-l dark:border-slate-700">
                  {/* Phase 9: AI Assistant Panel */}
                  {showAIPanel && (
                    <div className="p-4 border-b dark:border-slate-700">
                      <AIGeneratePanel
                        designId={currentDesign.id}
                        designName={currentDesign.name}
                        designDescription={currentDesign.description}
                        currentCaption={currentCaption}
                        onSelectCaption={(caption) => {
                          setCurrentCaption(caption);
                          toast.success('Caption selected - copy or use in your social post');
                        }}
                        onSelectHashtags={(hashtags) => {
                          navigator.clipboard.writeText(hashtags.join(' '));
                          toast.success('Hashtags copied to clipboard');
                        }}
                        isOpen={true}
                      />
                    </div>
                  )}
                  {/* Phase 7: Approval Panel */}
                  <div className="p-4">
                    <ApprovalPanel
                      entityType="design"
                      entityId={currentDesign.id}
                      entityName={currentDesign.name || 'Untitled Design'}
                      approvalStatus={currentDesign.approval_status || 'not_submitted'}
                      rejectionReason={currentDesign.rejection_reason}
                      revisionNotes={currentDesign.revision_notes}
                      submittedAt={currentDesign.submitted_for_approval_at}
                      approvedAt={currentDesign.approved_at}
                      rejectedAt={currentDesign.rejected_at}
                      onStatusChange={(newStatus) => {
                        setCurrentDesign(prev => ({...prev, approval_status: newStatus}));
                        fetchData(); // Refresh the designs list
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phase 8A: Dry Run Publish Dialog */}
      <DryRunPublish
        open={showDryRunDialog}
        onClose={() => setShowDryRunDialog(false)}
        entityType="design"
        entityId={currentDesign?.id}
        entityName={currentDesign?.name || 'Untitled Design'}
        approvalStatus={currentDesign?.approval_status || 'not_submitted'}
      />

      {/* Phase 8B: Live Publish Dialog */}
      <LivePublish
        open={showLivePublishDialog}
        onClose={() => setShowLivePublishDialog(false)}
        entityType="design"
        entityId={currentDesign?.id}
        entityName={currentDesign?.name || 'Untitled Design'}
        approvalStatus={currentDesign?.approval_status || 'not_submitted'}
        onPublishSuccess={() => {
          toast.success('Content published to social media!');
          fetchData();
        }}
      />

      <div className="p-6 space-y-6" data-testid="content-studio">
        <div>
          <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
            <PenTool className="h-8 w-8 text-purple-500" />
            Content Studio
          </h1>
          <p className="text-muted-foreground dark:text-slate-400 mt-1">
            Create graphics, blog posts, and social content
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="dark:bg-slate-800">
            <TabsTrigger value="designs" className="gap-2" data-testid="designs-tab">
              <Palette className="h-4 w-4" /> Designs ({designs.length})
            </TabsTrigger>
            <TabsTrigger value="ideas" className="gap-2">
              <Lightbulb className="h-4 w-4" /> Ideas ({ideas.length})
            </TabsTrigger>
            <TabsTrigger value="blog" className="gap-2">
              <FileText className="h-4 w-4" /> Blog Posts ({blogPosts.length})
            </TabsTrigger>
            <TabsTrigger value="social" className="gap-2">
              <Send className="h-4 w-4" /> Social Posts ({socialPosts.length})
            </TabsTrigger>
          </TabsList>

          {/* Designs Tab */}
          <TabsContent value="designs" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Create New Design Section */}
                <Card className="dark:bg-slate-800/60">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold dark:text-white">Create New Design</h3>
                        <p className="text-sm text-muted-foreground">Start from scratch or use a template</p>
                      </div>
                      <PermissionButton
                        permission={FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE}
                        onClick={() => setNewDesignDialog(true)}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="new-design-btn"
                        tooltipText="You don't have permission to create designs"
                      >
                        <Plus className="h-4 w-4 mr-2" /> New Design
                      </PermissionButton>
                    </div>
                  </CardContent>
                </Card>

                {/* Templates Section */}
                {templates.length > 0 && (
                  <div>
                    <h3 className="font-semibold dark:text-white mb-3 flex items-center gap-2">
                      <LayoutGrid className="h-4 w-4 text-purple-500" /> Templates
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      {templates.map(template => (
                        <Card 
                          key={template.id} 
                          className="dark:bg-slate-800/60 cursor-pointer hover:border-purple-500 transition-colors"
                          onClick={() => createFromTemplate(template)}
                        >
                          <CardContent className="p-3">
                            <div 
                              className="aspect-video rounded bg-slate-200 dark:bg-slate-700 mb-2 flex items-center justify-center"
                              style={{ backgroundColor: template.canvas_background }}
                            >
                              <Image className="h-8 w-8 text-slate-400" />
                            </div>
                            <p className="text-sm font-medium dark:text-white truncate">{template.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {template.canvas_width}x{template.canvas_height}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preset Sizes Quick Start */}
                <PermissionGate permission={FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE}>
                  <div>
                    <h3 className="font-semibold dark:text-white mb-3 flex items-center gap-2">
                      <LayoutGrid className="h-4 w-4 text-blue-500" /> Quick Start by Size
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {Object.entries(designPresets).map(([key, preset]) => (
                        <Button
                          key={key}
                          variant="outline"
                          className="h-auto py-3 flex-col gap-1 justify-center"
                          onClick={() => {
                            setNewDesignForm({ name: `New ${preset.name}`, design_type: key });
                            createDesign({ name: `New ${preset.name}`, design_type: key });
                          }}
                          data-testid={`preset-${key}`}
                        >
                          <span className="text-sm font-medium">{preset.name}</span>
                          <span className="text-xs text-muted-foreground">{preset.width}x{preset.height}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                </PermissionGate>

                {/* My Designs */}
                <div>
                  <h3 className="font-semibold dark:text-white mb-3 flex items-center gap-2">
                    <Palette className="h-4 w-4 text-purple-500" /> My Designs ({designs.length})
                  </h3>
                  {designs.length === 0 ? (
                    <Card className="dark:bg-slate-800/60">
                      <CardContent className="p-8 text-center">
                        <Palette className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                        <p className="text-muted-foreground">No designs yet. {canCreate ? 'Create your first design!' : ''}</p>
                        {canCreate && (
                          <Button 
                            className="mt-4 bg-purple-600 hover:bg-purple-700"
                            onClick={() => setNewDesignDialog(true)}
                          >
                            <Plus className="h-4 w-4 mr-2" /> Create Design
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {designs.map(design => (
                        <Card 
                          key={design.id} 
                          className="dark:bg-slate-800/60 cursor-pointer hover:border-purple-500 transition-colors group"
                          data-testid={`design-card-${design.id}`}
                        >
                          <CardContent className="p-0">
                            <div 
                              className="aspect-video rounded-t bg-slate-200 dark:bg-slate-700 flex items-center justify-center relative overflow-hidden"
                              style={{ backgroundColor: design.canvas_background }}
                              onClick={() => openDesign(design)}
                            >
                              {design.thumbnail_url ? (
                                <img src={design.thumbnail_url} alt={design.name} className="object-cover w-full h-full" />
                              ) : (
                                <Image className="h-12 w-12 text-slate-400" />
                              )}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <Button size="sm" className="bg-white text-black hover:bg-slate-100">
                                  <Edit className="h-3 w-3 mr-1" /> Edit
                                </Button>
                              </div>
                              {/* Phase 7: Approval Status Badge */}
                              {design.approval_status && design.approval_status !== 'not_submitted' && (
                                <div className="absolute top-2 right-2">
                                  <ApprovalStatusBadge status={design.approval_status} size="sm" />
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <p className="font-medium dark:text-white truncate">{design.name}</p>
                              <div className="flex items-center justify-between mt-2">
                                <Badge variant="secondary" className="text-xs">
                                  {designPresets[design.design_type]?.name || design.design_type}
                                </Badge>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {canCreate && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 w-7 p-0"
                                      onClick={(e) => { e.stopPropagation(); duplicateDesign(design.id); }}
                                      title="Duplicate"
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {canDelete && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                                      onClick={(e) => { e.stopPropagation(); deleteDesign(design.id); }}
                                      title="Delete"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {design.canvas_width}x{design.canvas_height} • Updated {new Date(design.updated_at).toLocaleDateString()}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ideas" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <IdeasTab ideas={ideas} onCreateIdea={createIdea} onDeleteIdea={deleteIdea} />
            )}
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <BlogPostsTab posts={blogPosts} onCreatePost={createBlogPost} onDeletePost={deleteBlogPost} />
            )}
          </TabsContent>

          <TabsContent value="social" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <SocialPostsTab posts={socialPosts} onCreatePost={createSocialPost} onDeletePost={deleteSocialPost} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* New Design Dialog */}
      <Dialog open={newDesignDialog} onOpenChange={setNewDesignDialog}>
        <DialogContent className="dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white">
              <Palette className="h-5 w-5 text-purple-500" />
              Create New Design
            </DialogTitle>
            <DialogDescription>
              Choose a name and size for your new design
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Design Name</Label>
              <Input
                value={newDesignForm.name}
                onChange={(e) => setNewDesignForm({ ...newDesignForm, name: e.target.value })}
                placeholder="My Awesome Design"
                className="dark:bg-slate-700"
              />
            </div>
            <div>
              <Label className="dark:text-slate-200">Design Type</Label>
              <Select
                value={newDesignForm.design_type}
                onValueChange={(v) => setNewDesignForm({ ...newDesignForm, design_type: v })}
              >
                <SelectTrigger className="dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(designPresets).map(([key, preset]) => (
                    <SelectItem key={key} value={key}>
                      {preset.name} ({preset.width}x{preset.height})
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom Size</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDesignDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => createDesign(newDesignForm)} 
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="create-design-submit"
            >
              Create Design
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
